import React, { useState } from 'react'
import { Button, Col, Popover, Radio, Row, Switch, Select } from 'antd'
import { CheckOutlined } from '@ant-design/icons'
import moment from 'moment'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { DataItem, MomentDatePicker } from '@gravitate-js/excalibrr'
import api from '@utils/api'
import { formatters } from '@utils/intl/dateTimeFormatters'

const { Option } = Select

// this is a simple toggle to handle booleans on the grid, and also handle their onChange event
export function DisableUntilToggle({ checked, record }) {
  const { scopes } = useSecurityContext()
  const [loading, setToggleLoading] = useState(false)
  const [toggleValue, setToggleValue] = useState(checked)
  const [disabledReasonOptions, setDisabledReasonOptions] = useState([])
  const [dateValue, setDateValue] = useState(
    record.disabled_until ? moment(record.disabled_until) : record.disabled_until
  )
  const canEditPrices = scopes.supply_directive?.price_management?.edit

  const fetchMetaData = async () => {
    return await api.getPriceMetadata()
  }

  const formatUntil = (date) => {
    const momentDate = moment(date)

    //  if its today we are just going to show time
    const isToday = momentDate.isSame(new Date(), 'day')
    if (isToday) {
      return formatters.TIME(momentDate)
    }
    return moment(date).format('MMM Do')
  }
  const cancelPopover = () => {
    setToggleLoading(false)
    setToggleValue(!toggleValue)
  }

  const onSave = (reason) => {
    setToggleLoading(false)

    api
      .savePrice({
        curve_id: record.curve_id,
        disabled: !toggleValue,
        disabled_until: dateValue ? moment(dateValue).toISOString() : null,
        disabled_reason: reason,
      })
      .then((resp) => {
        const data = resp.data
        record.disabled_until = data.disabled_until
        record.disabled_reason = data.disabled_reason
        record.enabled = !!toggleValue
        record.updated_by = data.updated_by
        record.updated_on = data.updated_on
        record.disabled_by = data.disabled_by
      })
      .catch(() => {
        setToggleLoading(false)
      })
  }
  const enablePrice = () => {
    setToggleLoading(false)
    setDateValue(null)
    api.savePrice({
      curve_id: record.curve_id,
      disabled: false,
    })
    delete record.disabled_until
    delete record.disabled_reason
    record.enabled = true
  }

  const handleToggle = () => {
    if (canEditPrices && toggleValue) {
      setToggleLoading(true)
      fetchMetaData().then((reasons) => {
        setDisabledReasonOptions(reasons.data.disabled_reasons)
      })
      setToggleValue(false)
    } else {
      setToggleValue(true)
      enablePrice()
    }
  }

  return (
    <>
      <Popover
        content={
          <UntilDateSelect
            dateValue={dateValue}
            setDateValue={setDateValue}
            onSave={onSave}
            onCancel={cancelPopover}
            canEditPrices={canEditPrices}
            disabledReasonOptions={disabledReasonOptions}
          />
        }
        title={<div className='disable-popover-header'>Disable Price Until</div>}
        trigger='click'
        visible={loading}
        placement='rightTop'
      >
        <Switch
          style={{ width: '100px' }}
          className='grid-toggle float-right'
          loading={loading}
          onClick={() => handleToggle()}
          checkedChildren={
            <span>
              <CheckOutlined className='switch-icon-fix' />
              <span className='switch-text-fix'>Enabled</span>
            </span>
          }
          unCheckedChildren={
            <span>
              <Popover
                title='Disabled Details'
                content={<DisabledPricePopoverContent disabledUntil={dateValue} record={record} />}
              >
                {loading ? 'Disabled' : dateValue ? formatUntil(dateValue) : 'Forever'}
              </Popover>
            </span>
          }
          checked={toggleValue}
        />
      </Popover>
    </>
  )
}

const UntilDateSelect = ({
  dateValue,
  setDateValue,
  disabledReason,
  onSave,
  onCancel,
  canEditPrices,
  disabledReasonOptions,
}) => {
  const [selectedButtonValue, setSelectedButtonValue] = useState()
  const [reasonValue, setReasonValue] = useState(disabledReason)

  const fillValue = (e) => {
    const selected = e.target.value
    setSelectedButtonValue(selected)
    if (selected === 'eod') {
      setDateValue(moment().add(1, 'days').set({ hour: 0, minute: 0, second: 0, millisecond: 0 }))
    }
    if (selected === 'eow') {
      setDateValue(moment().endOf('week').set({ hour: 0, minute: 0, second: 0, millisecond: 0 }))
    }
    if (selected === 'forever') {
      setDateValue(null)
    }
  }

  const dateOnChange = (date) => {
    setDateValue(date)
  }

  const checkInvalidDate = (current) => {
    return current && current <= moment().startOf('day')
  }

  return (
    <div className='disable-until'>
      <Radio.Group value={selectedButtonValue} onChange={fillValue}>
        <Radio.Button value='eod'>EOD</Radio.Button>
        <Radio.Button value='eow'>EOW</Radio.Button>
        <Radio.Button value='forever'>Forever</Radio.Button>
      </Radio.Group>
      {selectedButtonValue !== 'forever' ? (
        <MomentDatePicker
          format={formatters.DATE_TIME}
          showTime={{ format: formatters.DATE_TIME, defaultValue: moment().utc().local() }}
          onChange={dateOnChange}
          disabledDate={checkInvalidDate}
          value={dateValue}
          onOk={() => setSelectedButtonValue('')}
          style={{ width: '98%' }}
        />
      ) : (
        <div className='forever-status'>Forever</div>
      )}
      <Select
        labelInValue
        name='disabled_reason'
        placeholder='Choose Reason'
        className='mb-4'
        required
        style={{ width: '98%' }}
        onChange={(value) => {
          setReasonValue(value.value)
        }}
        defaultValue={reasonValue}
      >
        {disabledReasonOptions.map((value, index) => (
          <Option value={value} key={index}>
            {value}
          </Option>
        ))}
      </Select>

      <Row>
        <Col flex={1}>
          <Button type='link' style={{ color: '#BF2A45' }} onClick={() => onCancel()}>
            Cancel
          </Button>
        </Col>
        <Col>
          {canEditPrices && (
            <Button
              onClick={() => onSave(reasonValue)}
              className='disable-submit-button'
              disabled={(!reasonValue && !dateValue) || (!reasonValue && selectedButtonValue !== 'forever')}
            >
              Disable Price
            </Button>
          )}
        </Col>
      </Row>
    </div>
  )
}

const DisabledPricePopoverContent = ({ disabledUntil, record }) => {
  const disabledValue = disabledUntil !== null ? formatters.DATE_TIME(moment(disabledUntil)) : 'Forever'
  return (
    <>
      <Row>
        <Col>
          <DataItem label='Price Disabled:'>
            {record.updated_on ? formatters.DATE_TIME(moment(record.updated_on)) : 'Not Available'}
          </DataItem>
        </Col>
        <Col align='right' flex={1}>
          <DataItem label='Disabled Until:'>{disabledValue}</DataItem>
        </Col>
      </Row>
      <Row>
        <Col>
          <DataItem label='Reason Supply Disabled:'>{record.disabled_reason}</DataItem>
        </Col>
        <Col align='right' flex={1}>
          <DataItem label='Disabled By:'>{record.disabled_by}</DataItem>
        </Col>
      </Row>
    </>
  )
}
